<template>
  <section
    v-if="highlightData"
    class="new-highlights-input-section"
    :class="`new-highlights-input-section--${screenOrientationType}`"
    @click.stop="() => {}"
  >
    <label for="comment">
      <span class="new-highlights-input-section__title" v-text="title" />
      <span class="new-highlights-input-section__content">
        <highlights-card-section
          :key="videoPlayerKey"
          :highlightData="highlightData"
          :screenOrientationType="screenOrientationType"
        />

        <comment-input-section
          :targetEnableInput="true"
          :targetInputHighlightComment="targetInputHighlightComment"
          :postX="postX"
          :postInstagram="postInstagram"
          :hasShare="true"
          :editType="editType"
          :postedSns="postedSns"
          class="new-highlights-input-section__comment"
          @handlerSubmit="newHighlightsCard"
          @onChangePostX="onChangePostX"
          @onChangePostInstagram="onChangePostInstagram"
          @onChangeHighlightComment="changeHighlightComment"
          @onToggleEnabledInput="emitClose"
          @showHighlightPreviewVideoPlayer="showHighlightPreviewVideoPlayer"
          @hideHighlightPreviewVideoPlayer="hideHighlightPreviewVideoPlayer"
        />
      </span>
    </label>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref } from '@vue/composition-api'
import HighlightsCardSection from '@/components/RaceVideoPage/RaceVideoHighlightsPane/HighlightsCardSection.vue'
import { EditHighlightData } from '@/store/stores/pageStore/RaceVideoPage/RaceVideoPageStore'
import CommentInputSection from '@/components/RaceVideoPage/RaceVideoHighlightsPane/CommentInputSection.vue'
import i18n from '@/locales/I18n'
import { HighlightOperationType } from '@/components/RaceVideoPage/RaceVideoHighlightsPane.vue'

/**
 * レース動画再生画面 ハイライトカード新規作成/編集 入力セクション
 */
export default defineComponent({
  name: 'NewHighlightsInputSection',
  components: {
    HighlightsCardSection,
    CommentInputSection,
  },
  inject: ['loading'],
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    editType: {
      type: String as PropType<HighlightOperationType>,
      default: 'create',
    },
    /**
     * ハイライトの作成・編集で使用するデータ
     */
    highlightData: {
      type: Object as PropType<EditHighlightData>,
    },
    /**
     * プレビュー動画プレイヤー再描画用のキー
     */
    videoPlayerKey: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const targetInputHighlightComment = ref<string>('')
    // Xにハイライトを投稿するかどうか
    const postX = ref<boolean>(false)
    // Instagramにハイライトを投稿するかどうか
    const postInstagram = ref<boolean>(false)
    const screenLandscape = computed(() => props.screenOrientationType?.indexOf('landscape') !== -1)
    const viewportHeight = ref<number>(0)
    const title = computed(() => i18n.tc(`RaceVideoPage.highlights.${props.editType}.title`))
    // SNSに投稿したことがあるかどうか
    const postedSns = computed(
      () => props.highlightData?.editHighlightData.additionalData?.postedSns,
    )

    const changeHighlightComment = (val: string) => {
      targetInputHighlightComment.value = val
    }

    /**
     * ハイライト動画をXに投稿する/しないを切り替える
     */
    const onChangePostX = (val: boolean) => {
      postX.value = val
    }

    /**
     * ハイライト動画をInstagramに投稿する/しないを切り替える
     */
    const onChangePostInstagram = (val: boolean) => {
      postInstagram.value = val
    }

    onMounted(() => {
      targetInputHighlightComment.value = props.highlightData?.editHighlightData?.note || ''
    })

    return {
      targetInputHighlightComment,
      changeHighlightComment,
      viewportHeight,
      screenLandscape,
      title,
      postX,
      postInstagram,
      postedSns,
      onChangePostX,
      onChangePostInstagram,
    }
  },
  methods: {
    /**
     * 動画プレイヤーを表示する
     */
    showHighlightPreviewVideoPlayer() {
      this.$emit('showHighlightPreviewVideoPlayer')
    },
    /**
     * 動画プレイヤーを非表示にする
     */
    hideHighlightPreviewVideoPlayer() {
      this.$emit('hideHighlightPreviewVideoPlayer')
    },
    emitClose() {
      this.$emit('close')
    },
    /**
     * ハイライトカード作成
     */
    newHighlightsCard() {
      this.$emit(
        'onSaveHighlight',
        this.targetInputHighlightComment,
        this.highlightData?.editHighlightData,
        this.postX,
        this.postInstagram,
      )
    },
  },
})
</script>

<style lang="scss" scoped>
.new-highlights-input-section {
  width: calc(100% - 12px);
  position: absolute;
  bottom: 0;
  right: 6px;
  z-index: 41;

  &__title {
    display: flex;
    width: 100%;
    height: 45px;
    padding: 0 24px 1px;
    border-radius: 8px 8px 0 0;
    border-bottom: 1px solid $white10;
    justify-content: center;
    align-items: center;
    background: $black20;
    box-shadow: 0 1px 0 0 $black05;
    color: $white;
    font-family: $fontSignup;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 10px 6px;
    background: $black20;
  }
}
</style>

<style lang="scss">
.new-highlights-input-section {
  .comment-input-section__modal {
    top: auto;
    height: 100vh;
  }

  &--landscape-primary,
  &--landscape-secondary {
    .highlights-card-parts {
      width: 100%;
    }
  }
}
</style>
